'use client'

import { AccountMenuAboutPopup } from '../AccountMenuAboutPopup/AccountMenuAboutPopup'
import { AccountsIcon } from '@repo/ui/AccountsIcon'
import { Avatar } from '@repo/ui/Avatar'
import { DocumentIcon } from '@repo/ui/DocumentIcon'
import { EmailIcon } from '@repo/ui/EmailIcon'
import { InfoIcon } from '@repo/ui/InfoIcon'
import { LogoutIcon } from '@repo/ui/LogoutIcon'
import { OvertimeIcon } from '@repo/ui/OvertimeIcon'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'
import { useDashboardVideoStore } from '@repo/store/useDashboardVideoStore'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import { useHover, useMenuState, ControlledMenu, MenuItem, MenuDivider } from '@szhsin/react-menu'
import { useMenuItemsStore } from '@repo/store/useMenuItemsStore'
import { useNavigate } from 'react-router-dom'
import { UserIcon } from '@repo/ui/UserIcon'
import { useState, useEffect, useRef } from 'react'
import { WhatsNewIcon } from '@repo/ui/WhatsNewIcon'
import MUIBackdrop from '@mui/material/Backdrop'
import MUIIconButton from '@mui/material/IconButton'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'

export const AccountMenu = () => {
  const { emailAddress, userId } = useAuthenticationStore()
  const navigate = useNavigate()
  const { sidebarMode, companyCanUseMicrosoftEmail, useMicrosoftEmail } = useGlobalSettingsStore()
  const { enableExpenses, enableOvertime } = useLookupsStore()
  const { mainMenuItems, closeMainNavigationMenu, allowedActions } = useMenuItemsStore()
  const { submitExpenseClaims, submitOvertimeClaims } = allowedActions
  const submitExpenseClaimsAllowed = submitExpenseClaims?.allowed || false
  const submitOvertimeClaimsAllowed = submitOvertimeClaims?.allowed || false

  const [isOpen, setIsOpen] = useState(false)
  const [menuState, toggleMenu] = useMenuState({ transition: false })
  const { anchorProps, hoverProps } = useHover(menuState.state, toggleMenu)
  const ref = useRef(null)

  const { toggleVideoModal } = useDashboardVideoStore()

  const [aboutModalOpen, setAboutModalOpen] = useState(false)

  useEffect(() => {
    setIsOpen(menuState?.state?.includes('open'))
  }, [setIsOpen, menuState])

  useEffect(() => {
    if (isOpen && mainMenuItems) closeMainNavigationMenu()
  }, [isOpen, closeMainNavigationMenu, mainMenuItems])

  const goToAccountPage = async () => {
    navigate('/my-account/account-settings')
    setIsOpen(false)
  }

  const goToExpenseClaimsPage = async () => {
    navigate(`/my-account/expense-claims`)
    setIsOpen(false)
  }

  const goToOvertimeClaimsPage = async () => {
    navigate(`/my-account/overtime-claims`)
    setIsOpen(false)
  }

  const logout = async () => {
    navigate('/auth/logout')
  }

  const goToEmailConfigPage = async () => {
    navigate('/configuration/microsoft/email-setup')
  }

  const goToThirdPartyFormsPage = async () => {
    navigate('/configuration/third-party-forms')
  }

  return (
    <>
      <MUIIconButton
        {...anchorProps}
        title="Account menu"
        data-testid="accountmenubutton"
        ref={ref}
        sx={{
          width: sidebarMode === 'expanded' ? '264px' : '64px',
          position: 'relative',
          zIndex: 10,
          height: '73px'
        }}
      >
        <MUIStack
          direction="row"
          alignItems="center"
          justifyContent="flex-start"
          style={{ width: '100%', margin: '13px 12px 5px' }}
          gap={4}
        >
          <Avatar sx={{ pointerEvents: 'none' }} size={'36px'} name={emailAddress} />
          {sidebarMode === 'expanded' ? (
            <UserNameLockup userId={userId} emailAddress={emailAddress} color="white" />
          ) : null}
        </MUIStack>
      </MUIIconButton>

      <ControlledMenu
        {...menuState}
        {...hoverProps}
        anchorRef={ref}
        className={`accountMenu${companyCanUseMicrosoftEmail && useMicrosoftEmail ? ' includeEmailConfigButton' : ''}`}
        direction={'right'}
        arrow
        shift={200}
        portal
      >
        <MUIStack
          direction="row"
          alignItems="center"
          justifyContent="flex-start"
          sx={{ padding: '16px 0 8px' }}
          tabIndex={-1}
          role="menuitem"
          title="Account menu"
        >
          <Avatar sx={{ ml: 3, mr: 4 }} size={'42px'} name={emailAddress} />
          <UserNameLockup userId={userId} emailAddress={emailAddress} color="text.primary" />
        </MUIStack>

        <MenuDivider />
        <div role="menuitem" title="Account menu">
          <MUITypography variant="h6" sx={{ mt: 4, ml: 3, mb: 3, pointerEvents: 'none' }}>
            My account
          </MUITypography>
        </div>

        <MenuItem onClick={goToAccountPage}>
          <UserIcon />
          Account settings
        </MenuItem>

        {enableExpenses && (
          <MenuItem onClick={goToExpenseClaimsPage} disabled={!submitExpenseClaimsAllowed}>
            <AccountsIcon />
            Expense claims
          </MenuItem>
        )}

        {enableOvertime && (
          <MenuItem onClick={goToOvertimeClaimsPage} disabled={!submitOvertimeClaimsAllowed}>
            <OvertimeIcon />
            Overtime claims
          </MenuItem>
        )}

        {companyCanUseMicrosoftEmail && useMicrosoftEmail ? (
          <MenuItem onClick={goToEmailConfigPage}>
            <EmailIcon />
            Email setup
          </MenuItem>
        ) : null}

        <MenuItem onClick={goToThirdPartyFormsPage}>
          <DocumentIcon />
          Third party forms
        </MenuItem>

        <MenuItem onClick={() => setAboutModalOpen(true)}>
          <InfoIcon />
          About
        </MenuItem>
        <MenuItem onClick={() => toggleVideoModal(true)}>
          <WhatsNewIcon />
          {`What's new`}
        </MenuItem>

        <MenuDivider />

        <MenuItem onClick={logout}>
          <MUIStack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            style={{ width: '100%', paddingRight: '12px' }}
          >
            <LogoutIcon sx={{ mr: '10px !important' }} />
            <MUITypography variant="subtitle1" sx={{ mb: 0, pointerEvents: 'none' }}>
              Logout
            </MUITypography>
          </MUIStack>
        </MenuItem>
      </ControlledMenu>

      <AccountMenuAboutPopup open={aboutModalOpen} handleClose={() => setAboutModalOpen(false)} />

      <MUIBackdrop
        open={isOpen || false}
        onClick={() => toggleMenu(false)}
        sx={{ backgroundColor: 'rgb(0,0,0,0.1)', zIndex: 9 }}
      />
    </>
  )
}

const UserNameLockup = ({ userId, emailAddress, color }) => {
  return (
    <MUIStack direction="column" alignItems="flex-start" justifyContent="center" gap="3px" sx={{ maxWidth: '172px' }}>
      {userId ? (
        <MUITypography variant="h6" sx={{ m: 0, color: color, width: '100%', textAlign: 'left' }} noWrap>
          {userId}
        </MUITypography>
      ) : null}

      {emailAddress ? (
        <MUITypography variant="body2" sx={{ m: 0, color, width: '100%', textAlign: 'left' }} noWrap>
          {emailAddress}
        </MUITypography>
      ) : null}
    </MUIStack>
  )
}

UserNameLockup.propTypes = {
  userId: PropTypes.string,
  emailAddress: PropTypes.string,
  color: PropTypes.string
}
